<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">会员管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">会员信息</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">充值金额设置</span></h2>
      <el-button type="primary" @click="showDialog">+ 新增金额</el-button>
    </div>

    <!-- 会员等级达标提示栏 -->
    <div class="level-bar" v-if="memberLevels.length > 0">
      <div class="level-bar-title">充值达标可升级：</div>
      <div class="level-bar-items">
        <div v-for="lv in memberLevels" :key="lv.id" class="level-bar-item">
          <span class="level-name">{{ lv.level_name }}</span>
          <span class="level-amount">¥{{ lv.upgrade_amount }}</span>
          <span class="level-discount">{{ lv.discount }}折</span>
        </div>
      </div>
    </div>

    <!-- 充值金额卡片网格 -->
    <div class="recharge-grid" v-loading="loading">
      <div v-for="item in sortedList" :key="item.id" class="recharge-card" :class="{ disabled: !item.status }">
        <div class="card-top">
          <div class="card-amount">
            <span class="amount-symbol">¥</span>
            <span class="amount-num">{{ item.amount }}</span>
          </div>
        </div>
        <div class="card-meta">
          <span class="meta-item">排序: {{ item.sort_order }}</span>
        </div>
        <div class="card-actions">
          <el-switch v-model="item.status" size="small" @change="toggleStatus(item)" />
          <div class="card-btn-group">
            <el-button text size="small" @click="showEditDialog(item)">编辑</el-button>
            <el-button text type="danger" size="small" @click="handleDelete(item)">删除</el-button>
          </div>
        </div>
      </div>

      <!-- 新增卡片 -->
      <div class="recharge-card recharge-card-add" @click="showDialog">
        <div class="add-icon">+</div>
        <div class="add-text">新增金额</div>
      </div>
    </div>

    <el-dialog v-model="dialogVisible" title="新增充值金额" width="400px">
      <el-form ref="formRef" :model="form" :rules="{amount:[{required:true,message:'请输入金额'},{type:'number',min:0.01,message:'金额必须大于0'}]}" label-width="100px">
        <el-form-item label="金额(元)" prop="amount">
          <el-input-number v-model="form.amount" :precision="2" :min="0.01" :step="10" style="width:200px" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" style="width:200px" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible=false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as api from '../../api/userExtensions';
import { getMemberLevels } from '../../api/userExtensions';

const list = ref([]);
const memberLevels = ref([]);
const loading = ref(false);
const dialogVisible = ref(false);
const isEdit = ref(false);
const editingId = ref(null);
const saving = ref(false);
const formRef = ref(null);
const form = ref({ amount: null, sort_order: 0 });

// 按金额升序排列
const sortedList = computed(() => {
  return [...list.value].sort((a, b) => a.amount - b.amount);
});

const fetchData = async () => {
  loading.value = true;
  try {
    const r = await api.getRechargeAmounts();
    list.value = r.data || [];
  } catch {} finally { loading.value = false; }
};

const showDialog = () => {
  isEdit.value = false;
  editingId.value = null;
  form.value = { amount: null, sort_order: sortedList.value.length };
  dialogVisible.value = true;
};

const showEditDialog = (item) => {
  isEdit.value = true;
  editingId.value = item.id;
  form.value = { amount: item.amount, sort_order: item.sort_order };
  dialogVisible.value = true;
};

const toggleStatus = async (row) => {
  try { await api.toggleRechargeAmount(row.id); } catch {}
};

const handleSave = async () => {
  const v = await formRef.value.validate().catch(() => false);
  if (!v) return;
  saving.value = true;
  try {
    if (isEdit.value) {
      await api.updateRechargeAmount(editingId.value, form.value);
      ElMessage.success('更新成功');
    } else {
      await api.createRechargeAmount(form.value);
      ElMessage.success('新增成功');
    }
    dialogVisible.value = false;
    await fetchData();
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '操作失败');
  } finally { saving.value = false; }
};

const handleDelete = (r) => {
  ElMessageBox.confirm('确定删除该充值金额？', '提示').then(async () => {
    await api.deleteRechargeAmount(r.id);
    await fetchData();
  }).catch(() => {});
};

onMounted(async () => {
  try {
    const r = await getMemberLevels();
    memberLevels.value = (r.data || []).filter(lv => lv.status !== false);
  } catch {}
  await fetchData();
});
</script>

<style scoped>
.page-header h2 {
  color: #303133;
}

/* ===== 会员等级达标提示栏 ===== */
.level-bar {
  background: linear-gradient(135deg, #FFFCF7 0%, #FFF5E6 100%);
  border: 1px solid #E8D5B5;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.level-bar-title {
  font-size: 14px;
  font-weight: 600;
  color: #9E8569;
  white-space: nowrap;
}

.level-bar-items {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.level-bar-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #E8D5B5;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
}

.level-name {
  font-weight: 600;
  color: #9E8569;
}

.level-amount {
  color: #C9A86C;
  font-weight: 500;
}

.level-discount {
  color: #E8AD6A;
  font-size: 12px;
}

/* ===== 卡片网格 ===== */
.recharge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

/* ===== 充值卡片 ===== */
.recharge-card {
  background: #FFFCF7;
  border: 2px solid #E8D5B5;
  border-radius: 12px 36px 12px 12px;
  padding: 20px;
  position: relative;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recharge-card:hover {
  border-color: #C9A86C;
  box-shadow: 0 4px 16px rgba(201, 168, 108, 0.2);
  transform: translateY(-2px);
}

.recharge-card.disabled {
  opacity: 0.55;
  border-style: dashed;
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.card-amount {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.amount-symbol {
  font-size: 18px;
  font-weight: 600;
  color: #9E8569;
  line-height: 1;
}

.amount-num {
  font-size: 36px;
  font-weight: 700;
  color: #9E8569;
  line-height: 1;
}

.card-meta {
  font-size: 13px;
  color: #909399;
}

.card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid #f0e8d8;
}

/* ===== 新增卡片 ===== */
.recharge-card-add {
  border: 2px dashed #d9d9d9;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 140px;
}

.recharge-card-add:hover {
  border-color: #C9A86C;
  background: #FFFCF7;
  box-shadow: 0 4px 16px rgba(201, 168, 108, 0.15);
  transform: none;
}

.add-icon {
  font-size: 36px;
  font-weight: 300;
  color: #C9A86C;
  line-height: 1;
}

.add-text {
  font-size: 14px;
  color: #909399;
}
</style>